import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ConnectComponentsProvider, ConnectNotificationBanner } from '@stripe/react-connect-js';
import useAccountSession from '../../connect/use-account-session';
import useConnectInstance from '../../connect/use-connect-instance';

// Kept only to key useConnectInstance's effect (use-connect-instance.js: the
// list is never sent to Stripe or to our server — the server decides what a
// "finance" session carries, notification_banner included).
const NOTICE_COMPONENTS = ['notification_banner'];

// Stripe's own "Action required" box (the one Finance > Overview draws from
// finance-section.js), on the Dashboard home. Same component, so its buttons
// work in place.
//
// `companyId` is the one decision the caller makes: null means "this viewer
// is not meant to see Stripe's box", and then nothing is minted. The caller
// (DashboardAlerts) owns who qualifies; this component never decides that.
//
// Nothing to show means nothing drawn. Stripe reports its count only after
// the banner has mounted and loaded, so the banner is mounted from the start
// in a wrapper with zero height and `overflow-hidden` — not `display:none`,
// which would stop the iframe from being laid out at all. The wrapper is
// absolutely positioned while empty so it does not take a slot (or a gap) in
// the alert list's flex column either. Once Stripe says there is something to
// show, the wrapper joins the flow.
//
// A session or instance failure draws nothing: the Dashboard is not the place
// for a retry button, and the alerts around this box still render.
const StripeNotices = ({ companyId }) => {
  const isDark = useSelector((state) => state.commonReducer.isDark);
  const { fetchSecret, error: sessionError } = useAccountSession(
    companyId || null,
    'finance',
  );
  // The company is part of the instance's identity. Today a company switch
  // reloads the page, and the store clears its Stripe status while the new
  // company's read is pending — this does not rely on either: a different
  // company always builds a new instance and so mints its own session.
  const components = useMemo(
    () => [...NOTICE_COMPONENTS, `company-${companyId}`],
    [companyId],
  );
  const { instance } = useConnectInstance(
    companyId ? fetchSecret : null,
    components,
    isDark,
  );

  // Both facts are remembered with the company they were reported for, so an
  // answer about company A is never read as company B's.
  const [reported, setReported] = useState({ companyId: null, total: 0, failed: false });
  const onNotificationsChange = useCallback(
    ({ total }) => setReported((prev) => ({
      companyId,
      total: total || 0,
      failed: prev.companyId === companyId && prev.failed,
    })),
    [companyId],
  );
  const onLoadError = useCallback(
    () => setReported({ companyId, total: 0, failed: true }),
    [companyId],
  );
  const current = reported.companyId === companyId ? reported : null;
  const total = current?.total || 0;

  // An instance that could not be built comes back as `instance: null`, so
  // that case is already covered by the first line.
  if (!companyId || !instance) return null;
  if (sessionError || current?.failed) return null;

  const collapsed = total <= 0;
  // While collapsed the clipped iframe must not take keyboard focus or be
  // read out: aria-hidden + inert. React 18.2 has no `inert` prop; the
  // string form `inert=""` reaches the DOM without a warning (a boolean
  // true is warned about), and undefined removes it once there is a notice.
  return (
    <div
      data-testid="stripe-notices"
      className={collapsed ? 'absolute inset-x-0 top-0 h-0 overflow-hidden' : undefined}
      aria-hidden={collapsed ? 'true' : undefined}
      inert={collapsed ? '' : undefined}
    >
      <ConnectComponentsProvider connectInstance={instance}>
        <ConnectNotificationBanner
          onNotificationsChange={onNotificationsChange}
          onLoadError={onLoadError}
        />
      </ConnectComponentsProvider>
    </div>
  );
};

export default StripeNotices;
